import { PStyled, QuotStyled } from 'approot/shared/styles/typography.style';
import SimpleModal from 'lib/modal/simple-modal';
import { ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE } from 'approot/activity-builder/activity-builder.constants';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { NegativeButton } from 'approot/shared/styles/buttons';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import { VocabularyThumbnailUpload } from './vocabulary-thumbnail';
import { ImageData } from './vocabulary-setting.constants';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { VocabularySearchApproved } from './vocabulary-search.style';
import IconTick from 'approot/shared/icons/icon-tick';
import { useVocabularySettingContext } from './vocabulary-setting.context';
import { getLanguageLabel } from 'approot/activity-builder/components/vocabulary/settings/vocabulary-setting.utils';
import { LanguageCodes } from 'global/constants';

export const ExampleImageModal = ({
  index,
  imageData,
  language,
  isViewOnly,
  onConfirm,
  onClose,
  word,
  example,
}: {
  index: number;
  imageData?: ImageData;
  language?: LanguageCodes;
  isViewOnly?: boolean;
  onConfirm: (imageData: ImageData) => void;
  onClose: () => void;
  word: string;
  example: string;
}) => {
  return (
    <SimpleModal
      openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      isVisible
      size={365}
      title={`Example ${index + 1} Image`}
      headerTitle={`Example ${index + 1} Image`}
      onClose={onClose}
    >
      <SimpleModalContentStyled>
        <FileDropperContextProvider>
          <VocabularyThumbnailUpload
            imageData={imageData}
            language={language}
            isDisabled={isViewOnly}
            onUpdate={data => {
              onConfirm(data);
              onClose();
            }}
            word={word}
            example={example}
            fileKey={imageData?.key}
          />
        </FileDropperContextProvider>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export const DeleteExampleConfirm = ({
  index,
  onConfirm,
  onClose,
}: {
  index: number;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  return (
    <SimpleModal
      openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      isVisible
      title="Delete Example"
      size={360}
      headerTitle="Delete Example"
      onClose={onClose}
    >
      <SimpleModalContentStyled>
        <PStyled marginBottom={0}>
          Are you sure you want to delete Example {index + 1}? Your example
          image and sentence will be lost.
        </PStyled>
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="button"
            onClick={onConfirm}
            data-selector="modal-btn-delete"
          >
            Delete
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export const DeleteWordConfirm = ({
  word,
  isLoading,
  onConfirm,
  onClose,
}: {
  word: string;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const { contentLabel } = useVocabularySettingContext();
  return (
    <SimpleModal
      openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      isVisible
      title={`Delete ${word}`}
      size={360}
      headerTitle={`Delete ${word}`}
      onClose={onClose}
    >
      <SimpleModalContentStyled>
        <PStyled marginBottom={0}>
          Are you sure you want to remove {word} from this {contentLabel}?
        </PStyled>
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="button"
            loading={isLoading}
            disabled={isLoading}
            onClick={onConfirm}
            data-selector="modal-btn-remove"
          >
            Remove from {contentLabel}
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export const DeleteWordFromDictionaryConfirm = ({
  word,
  isLoading,
  onConfirm,
  onClose,
}: {
  word: string;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  return (
    <SimpleModal
      openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      isVisible
      title={`Delete ${word}`}
      size={360}
      headerTitle={`Delete ${word}`}
      onClose={onClose}
    >
      <SimpleModalContentStyled>
        <PStyled marginBottom={0}>
          Are you sure you want to delete {word} from the database? This also
          removes the word from any Lesson or Assessment that it has already
          been added to.
        </PStyled>
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="button"
            loading={isLoading}
            disabled={isLoading}
            onClick={onConfirm}
          >
            Delete from Database
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export const RemoveTranslationFromDictionaryConfirm = ({
  word,
  language,
  isLoading,
  onConfirm,
  onClose,
}: {
  word: string;
  language: LanguageCodes;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const languageLabel = getLanguageLabel(language);
  return (
    <SimpleModal
      openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
      isVisible
      title={`Remove ${languageLabel}`}
      size={360}
      headerTitle={`Remove ${languageLabel}`}
      onClose={onClose}
    >
      <SimpleModalContentStyled>
        <PStyled marginBottom={0}>
          Are you sure you want to remove the {languageLabel} translations for{' '}
          <QuotStyled>{word}</QuotStyled>?
        </PStyled>
        <SimpleModalVerticalActionsStyled>
          <NegativeButton
            type="button"
            loading={isLoading}
            disabled={isLoading}
            onClick={onConfirm}
            data-selector="modal-btn-remove"
          >
            Remove {languageLabel}
          </NegativeButton>
        </SimpleModalVerticalActionsStyled>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export const VocabularyApprovedTooltip = () => (
  <TooltipWrapper hoverText="Approved by Inquisitive">
    <VocabularySearchApproved data-selector="icon-tick">
      <IconTick width={9} height={9} />
    </VocabularySearchApproved>
  </TooltipWrapper>
);
